import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Broadcast,
  Eye,
  Coins,
  User,
  Play,
  Stop,
  Target,
  VideoCamera,
  VideoCameraSlash,
  Microphone,
  MicrophoneSlash,
  CellSignalHigh,
  CellSignalSlash,
  X,
  ArrowLeft,
  Record,
  Rewind,
  Clock,
  DownloadSimple,
  MapPin,
  ShieldWarning,
  CaretDown,
  Check,
  CornersOut,
  CornersIn,
  ChatCircleText,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { toast } from 'sonner';
import StreamAppsConfigurator from '../components/streaming/StreamAppsConfigurator';
import ChatPanel from '../components/live/ChatPanel';
import TipAlert from '../components/live/TipAlert';
import TipLeaderboard from '../components/live/TipLeaderboard';
import PrivateShowViewerWidget from '../components/live/PrivateShowViewerWidget';
import PrivateShowCreatorWidget from '../components/live/PrivateShowCreatorWidget';
import GoLiveDialog from '../components/live/GoLiveDialog';
import SaveRecordingDialog from '../components/live/SaveRecordingDialog';
import ReplayViewer from '../components/live/ReplayViewer';
import ViewerPreviewPIP from '../components/live/ViewerPreviewPIP';
import EdgeStreamAppsOverlay from '../components/live/EdgeStreamAppsOverlay';
import Hls from 'hls.js';

const API = process.env.REACT_APP_BACKEND_URL;

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

/* ═══════════════════════════════════════════════
   MAIN LIVE PAGE
   ═══════════════════════════════════════════════ */
const LivePage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Mode: lobby | broadcasting | viewing
  const [mode, setMode] = useState('lobby');

  // Lobby state
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Go-live dialog
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [streamTitle, setStreamTitle] = useState('');
  const [streamDesc, setStreamDesc] = useState('');
  const [tokenGoal, setTokenGoal] = useState(0);
  const [recordVod, setRecordVod] = useState(true);   // VOD opt-out toggle in Go-Live dialog
  const [starting, setStarting] = useState(false);

  // Orphan-stream recovery (set when server reports an active stream we don't know about)
  const [orphanStream, setOrphanStream] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(false);

  // Geo-blocking
  // Geo-blocking — full editor lives in /settings; we keep just the user's
  // saved blocks here so the Go-Live dialog can show "N regions blocked".
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [blockedStates, setBlockedStates] = useState([]);

  // Stream state
  const [myStreamId, setMyStreamId] = useState(null);
  const [activeStream, setActiveStream] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  // Media
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');

  // Tips
  const [tipAmount, setTipAmount] = useState(5);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tipAlert, setTipAlert] = useState(null);
  const [tokensReceived, setTokensReceived] = useState(0);

  // Stream Apps (Chaturbate-style overlays)
  const [pendingApps, setPendingApps] = useState([]);
  const [showAppsPanel, setShowAppsPanel] = useState(false);

  // Guests on stream (declared before going live for 2257 compliance)
  const [streamGuests, setStreamGuests] = useState([]);
  const [showGuestsPanel, setShowGuestsPanel] = useState(false);
  const [myPerformerId, setMyPerformerId] = useState(null);

  // Recording
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);

  // Preview-as-viewer PIP (broadcaster only, mid-stream)
  const [showViewerPreview, setShowViewerPreview] = useState(false);
  const broadcastVideoContainerRef = useRef(null);

  // Viewer fullscreen mode (with chat sidebar overlay)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenChat, setShowFullscreenChat] = useState(true);

  // OBS: track the active OBS stream (creator monitors chat/tips while OBS ingests)
  const [obsMonitorStream, setObsMonitorStream] = useState(null);
  const obsEndPollRef = useRef(null);

  // VOD (replay) pricing chosen at save time
  const [vodAccess, setVodAccess] = useState('free'); // 'free' | 'paid' | 'subscribers_only'
  const [vodPrice, setVodPrice] = useState(0);

  // Replays
  const [lobbyTab, setLobbyTab] = useState('live'); // 'live' | 'replays'
  const [replays, setReplays] = useState([]);
  const [loadingReplays, setLoadingReplays] = useState(false);
  const [watchingReplay, setWatchingReplay] = useState(null);

  // ICE servers (fetched from backend, includes TURN if configured)
  const [iceServers, setIceServers] = useState(ICE_SERVERS);

  // Source mode: 'camera' | 'screen'
  const [sourceMode, setSourceMode] = useState('camera');

  // Stream mode: 'browser' (WebRTC) | 'obs' (RTMPS ingest)
  const [streamMode, setStreamMode] = useState('browser');

  // OBS RTMPS key/URL data (fetched when user picks OBS mode)
  const [obsKeyData, setObsKeyData] = useState(null);
  const [showObsKey, setShowObsKey] = useState(false);
  const [obsGoingLive, setObsGoingLive] = useState(false);

  // Camera / mic device pickers (populated after getUserMedia permission)
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [selectedAudioId, setSelectedAudioId] = useState('');

  // Camera preview in Go Live dialog
  const previewVideoRef = useRef(null);
  const previewStreamRef = useRef(null);

  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const replayVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const peerConnectionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingStartTimeRef = useRef(null);
  const endedStreamIdRef = useRef(null);

  /* ───────── Fetch own Performer ID when go-live dialog opens ───────── */
  useEffect(() => {
    if (!goLiveOpen) return;
    if (myPerformerId) return;
    axios.get(`${API}/api/verification/my-performer-id`, { withCredentials: true })
      .then(r => setMyPerformerId(r.data.performer_id))
      .catch(() => { /* unverified or admin — silent */ });
  }, [goLiveOpen, myPerformerId]);

  /* ───────── Lovense SDK loader (broadcaster only) ───────── */
  const lovenseLoadedRef = useRef(false);
  useEffect(() => {
    if (mode !== 'broadcasting') return;
    if (lovenseLoadedRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const mintRes = await axios.post(`${API}/api/lovense/mint-token`, {}, { withCredentials: true });
        if (cancelled) return;
        const mToken = mintRes.data?.m_token;
        if (!mToken) return;

        // Inject Lovense model.js SDK with mToken
        const existing = document.querySelector('script[data-lovense="model-js"]');
        if (existing) existing.remove();

        const script = document.createElement('script');
        script.src = `https://api.lovense-api.com/api/cam/model.js?mToken=${encodeURIComponent(mToken)}`;
        script.async = true;
        script.dataset.lovense = 'model-js';
        script.onload = () => {
          lovenseLoadedRef.current = true;
          console.info('[Lovense] model.js loaded');
        };
        script.onerror = () => {
          console.warn('[Lovense] model.js failed to load');
        };
        document.body.appendChild(script);
      } catch (err) {
        // 503 = platform not configured. 400 = creator hasn't set model_id. Either is silent for non-Lovense users.
        const code = err?.response?.status;
        if (code && code !== 503 && code !== 400) {
          console.warn('[Lovense] mint-token failed:', err?.response?.data?.detail || err?.message);
        }
      }
    })();

    return () => {
      cancelled = true;
      const script = document.querySelector('script[data-lovense="model-js"]');
      if (script) script.remove();
      lovenseLoadedRef.current = false;
      // Best-effort SDK teardown
      if (window.Lovense && typeof window.Lovense.destroy === 'function') {
        try { window.Lovense.destroy(); } catch { /* noop */ }
      }
    };
  }, [mode]);

  /* ───────── HLS playback for OBS/Cloudflare-mode streams ───────── */
  const hlsRef = useRef(null);
  useEffect(() => {
    if (mode !== 'viewing') return;
    if (!activeStream || activeStream.mode !== 'obs' || !activeStream.playback_hls) return;
    const video = remoteVideoRef.current;
    if (!video) return;

    let hls = null;
    const url = activeStream.playback_hls;
    if (Hls.isSupported()) {
      hls = new Hls({ lowLatencyMode: true, backBufferLength: 30 });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => { /* autoplay can be blocked; user can click play */ });
        setConnectionStatus('streaming');
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          console.warn('[LivePage] HLS fatal:', data.type, data.details);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        }
      });
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = url;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(() => {});
        setConnectionStatus('streaming');
      });
    } else {
      toast.error('Your browser does not support HLS playback');
    }

    return () => {
      if (hls) {
        try { hls.destroy(); } catch { /* noop */ }
        hlsRef.current = null;
      }
      if (video) {
        video.removeAttribute('src');
        try { video.load(); } catch { /* noop */ }
      }
    };
  }, [mode, activeStream]);

  /* ───────── Lobby: Fetch streams ───────── */
  const fetchStreams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/streaming/live`, { withCredentials: true });
      setStreams(res.data);
    } catch (err) {
      console.warn('[LivePage] fetchStreams failed:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ───────── Recovery: detect orphaned live stream on mount ─────────
     If we're in the lobby AND not actively broadcasting, ask the server
     "do I own a live stream right now?". If yes, surface the recovery
     banner so the user can resume or force-end. Prevents the
     "you already have a live stream" lockout when local state was lost. */
  const checkForOrphanStream = useCallback(async () => {
    if (!user || (user.role !== 'creator' && user.role !== 'admin')) return;
    try {
      const res = await axios.get(`${API}/api/streaming/my-active`, { withCredentials: true });
      setOrphanStream(res.data?.stream || null);
    } catch { /* silent — non-critical */ }
  }, [user]);

  useEffect(() => {
    if (mode === 'lobby') {
      fetchStreams();
      checkForOrphanStream();
      const interval = setInterval(fetchStreams, 8000);
      return () => clearInterval(interval);
    }
  }, [mode, fetchStreams, checkForOrphanStream]);

  /* ───────── Orphan stream: resume the existing live session ───────── */
  const resumeOrphanStream = useCallback(() => {
    if (!orphanStream) return;
    setMyStreamId(orphanStream.stream_id);
    setMode('broadcasting');
    setOrphanStream(null);
    toast.success('Resumed broadcasting session');
  }, [orphanStream]);

  /* ───────── Orphan stream: force-end ALL of caller's live streams ───────── */
  const forceEndOrphanStream = useCallback(async () => {
    setOrphanBusy(true);
    try {
      const res = await axios.post(`${API}/api/streaming/end-all-mine`, {}, { withCredentials: true });
      toast.success(`Force-ended ${res.data.ended_count} live stream(s)`);
      setOrphanStream(null);
      fetchStreams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force-end. Please refresh and try again.');
    } finally {
      setOrphanBusy(false);
    }
  }, [fetchStreams]);

  /* ───────── Lobby: Fetch replays ───────── */
  const fetchReplays = useCallback(async () => {
    setLoadingReplays(true);
    try {
      const res = await axios.get(`${API}/api/streaming/recordings/browse`, { withCredentials: true });
      setReplays(res.data);
    } catch (err) {
      console.warn('[LivePage] fetchReplays failed:', err?.message);
    } finally {
      setLoadingReplays(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'lobby' && lobbyTab === 'replays') {
      fetchReplays();
    }
  }, [mode, lobbyTab, fetchReplays]);

  /* ───────── Geo-blocking: Fetch user blocks for the Go-Live summary ─────────
     The full editor lives in /settings now; here we only need the count to
     render "N regions blocked" on the Go-Live dialog. Available to both
     creators and admins (admins can also broadcast). */
  useEffect(() => {
    if (user?.role === 'creator' || user?.role === 'admin') {
      const loadGeo = async () => {
        try {
          const blocksRes = await axios.get(`${API}/api/geo/my-blocks`, { withCredentials: true });
          setBlockedCountries(blocksRes.data.blocked_countries || []);
          setBlockedStates(blocksRes.data.blocked_states || []);
        } catch { /* silent */ }
      };
      loadGeo();
    }
  }, [user?.role]);

  /* ───────── Fetch ICE servers (includes TURN if configured) ───────── */
  useEffect(() => {
    const fetchIce = async () => {
      try {
        const res = await axios.get(`${API}/api/streaming/ice-servers`);
        if (res.data.ice_servers?.length) setIceServers(res.data.ice_servers);
      } catch { /* use defaults */ }
    };
    fetchIce();
  }, []);

  /* ───────── Camera preview + device enumeration for Go Live dialog ───────── */
  useEffect(() => {
    if (!goLiveOpen) {
      // Cleanup preview when dialog closes
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach(t => t.stop());
        previewStreamRef.current = null;
      }
      return;
    }
    if (streamMode === 'obs') return; // No preview / device pickers in OBS mode

    const startPreview = async () => {
      try {
        const videoConstraint = sourceMode === 'screen'
          ? { displaySurface: 'monitor' }
          : (selectedVideoId ? { deviceId: { exact: selectedVideoId } } : true);
        const audioConstraint = selectedAudioId ? { deviceId: { exact: selectedAudioId } } : true;
        const stream = sourceMode === 'screen'
          ? await navigator.mediaDevices.getDisplayMedia({ video: videoConstraint, audio: true })
          : await navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: audioConstraint });
        previewStreamRef.current = stream;
        if (previewVideoRef.current) previewVideoRef.current.srcObject = stream;

        // After permission is granted, enumerate devices (labels are populated only after permission).
        try {
          const list = await navigator.mediaDevices.enumerateDevices();
          const vids = list.filter(d => d.kind === 'videoinput');
          const auds = list.filter(d => d.kind === 'audioinput');
          setVideoDevices(vids);
          setAudioDevices(auds);
          if (!selectedVideoId && vids[0]) setSelectedVideoId(vids[0].deviceId);
          if (!selectedAudioId && auds[0]) setSelectedAudioId(auds[0].deviceId);
        } catch { /* silent */ }
      } catch {
        // Permission denied or no device — silent
      }
    };
    startPreview();
    return () => {
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach(t => t.stop());
        previewStreamRef.current = null;
      }
    };
  }, [goLiveOpen, sourceMode, selectedVideoId, selectedAudioId, streamMode]);

  /* ───────── Fetch OBS stream key when user switches to OBS mode ───────── */
  useEffect(() => {
    if (!goLiveOpen || streamMode !== 'obs' || obsKeyData) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/store/stream-key`, { withCredentials: true });
        setObsKeyData(res.data);
      } catch { toast.error('Failed to load OBS stream key'); }
    })();
  }, [goLiveOpen, streamMode, obsKeyData]);

  /* ───────── Start OBS broadcast (creates the stream record so /live shows it) ───────── */
  const handleStartObs = async () => {
    if (!streamTitle.trim()) { toast.error('Enter a stream title'); return; }
    setObsGoingLive(true);
    try {
      const res = await axios.post(
        `${API}/api/streaming/start-obs`,
        { title: streamTitle, description: streamDesc, token_goal: tokenGoal },
        { withCredentials: true },
      );
      const streamId = res.data.stream_id;

      // Apply stream apps if the creator picked any in the dialog
      if (pendingApps.length > 0) {
        try {
          await axios.put(`${API}/api/stream-apps/stream/${streamId}`,
            { apps: pendingApps }, { withCredentials: true });
        } catch (e) { console.error('Failed to apply stream apps to OBS stream:', e); }
      }

      // Enter monitor mode: connect WebSocket for chat/tips + poll for stream end
      setMyStreamId(streamId);
      setActiveStream({
        stream_id: streamId,
        title: streamTitle,
        description: streamDesc,
        creator_name: user?.display_name || 'You',
        token_goal: tokenGoal,
        playback_hls: res.data.playback_hls,
        mode: 'obs',
      });
      setMode('obs_monitoring');
      connectWebSocket(streamId, 'broadcaster');
      setGoLiveOpen(false);
      toast.success('OBS stream is live. Chat + tips are now active on this page.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start OBS stream');
    } finally { setObsGoingLive(false); }
  };

  /* ───────── OBS end detection: poll active streams; if ours disappears, wrap up ───────── */
  useEffect(() => {
    if (mode !== 'obs_monitoring' || !myStreamId) return;
    obsEndPollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/streaming/live`, { withCredentials: true });
        const stillLive = (r.data || []).some(s => s.stream_id === myStreamId);
        if (!stillLive) {
          clearInterval(obsEndPollRef.current);
          toast.info('OBS stopped streaming. Ending session…');
          cleanupAndReturnToLobby();
        }
      } catch { /* transient — keep polling */ }
    }, 20000);
    return () => { if (obsEndPollRef.current) clearInterval(obsEndPollRef.current); };
  }, [mode, myStreamId]);

  /* ───────── Recording: detect best codec ───────── */
  const getRecordingMimeType = () => {
    const types = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
    return types.find(t => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) || 'video/webm';
  };

  /* ───────── Recording: start ───────── */
  const startRecording = (stream) => {
    try {
      const mimeType = getRecordingMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.start(5000);
      mediaRecorderRef.current = recorder;
      recordingStartTimeRef.current = Date.now();
    } catch {
      console.warn('MediaRecorder not supported');
    }
  };

  /* ───────── Recording: stop ───────── */
  const stopRecording = () => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') { resolve(null); return; }
      recorder.onstop = () => {
        const duration = (Date.now() - (recordingStartTimeRef.current || Date.now())) / 1000;
        setRecordingDuration(Math.round(duration));
        const blob = new Blob(recordingChunksRef.current, { type: recorder.mimeType || 'video/webm' });
        resolve(blob);
      };
      recorder.stop();
    });
  };

  /* ───────── Recording: upload ───────── */
  const uploadRecording = async (blob) => {
    if (!blob || !endedStreamIdRef.current) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      formData.append('title', streamTitle || 'Stream Recording');
      formData.append('duration', String(recordingDuration));
      const res = await axios.post(
        `${API}/api/streaming/recording/upload/${endedStreamIdRef.current}`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (p) => setUploadProgress(Math.round((p.loaded / (p.total || 1)) * 100)),
        }
      );
      const recordingId = res?.data?.recording_id;
      // Save pricing tier if creator chose anything other than the default
      if (recordingId && (vodAccess !== 'free' || vodPrice > 0)) {
        try {
          await axios.post(
            `${API}/api/streaming/recording/${recordingId}/pricing`,
            { price_tokens: vodAccess === 'paid' ? vodPrice : 0, access: vodAccess },
            { withCredentials: true },
          );
        } catch (err) {
          toast.error('Saved replay, but pricing update failed. You can set it from your profile.');
        }
      }
      toast.success('Recording saved as replay!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save recording');
    } finally {
      setUploading(false);
      setShowSaveDialog(false);
      setVodAccess('free');
      setVodPrice(0);
      recordingChunksRef.current = [];
      endedStreamIdRef.current = null;
      cleanupAndReturnToLobby();
    }
  };

  /* ───────── Re-attach local MediaStream when broadcaster view mounts ─────────
   * When mode flips to 'broadcasting', React re-mounts the <video ref={localVideoRef}>
   * element. srcObject that was set during handleGoLive gets lost because it was
   * assigned to a stale ref. This effect re-hydrates it on every mode change.
   */
  useEffect(() => {
    if (mode === 'broadcasting' && localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
      const p = localVideoRef.current.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay may block silently */ });
    }
  }, [mode]);

  /* ───────── WebSocket connection with auto-reconnect ───────── */
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const connectWebSocket = useCallback((streamId, role) => {
    // Compute absolute WebSocket URL (WebSocket API doesn't support relative URLs)
    let wsBase;
    if (API) {
      wsBase = API.replace('https://', 'wss://').replace('http://', 'ws://');
    } else {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsBase = `${proto}//${window.location.host}`;
    }
    const token = localStorage.getItem('lustlink_access_token') || '';
    const ws = new WebSocket(`${wsBase}/api/ws/stream/${streamId}?role=${role}&token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnectionStatus('connecting');
      reconnectAttemptsRef.current = 0;
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleSignalingMessage(data, role);
    };

    ws.onerror = () => {
      setConnectionStatus('error');
    };

    ws.onclose = (event) => {
      if (event.code === 4001) { toast.error('Authentication failed'); setConnectionStatus('disconnected'); return; }
      if (event.code === 4004) { toast.error('Stream not available'); setConnectionStatus('disconnected'); return; }

      // Auto-reconnect for unexpected disconnections
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && mode !== 'lobby') {
        setConnectionStatus('reconnecting');
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          connectWebSocket(streamId, role);
        }, delay);
      } else {
        setConnectionStatus('disconnected');
      }
    };

    return ws;
  }, [mode]);

  /* ───────── WebRTC: Handle signaling messages ───────── */
  const handleSignalingMessage = useCallback((data, role) => {
    switch (data.type) {
      case 'connected':
        setConnectionStatus('connected');
        if (data.chat_history) setChatMessages(data.chat_history);
        break;

      case 'viewer_joined':
        // Broadcaster: create RTCPeerConnection for this viewer & send offer
        createOfferForViewer(data.viewer_id);
        setViewerCount(data.viewer_count);
        break;

      case 'viewer_left':
        // Broadcaster: clean up PC for this viewer
        cleanupViewerPC(data.viewer_id);
        setViewerCount(data.viewer_count);
        break;

      case 'viewer_count':
        setViewerCount(data.count);
        break;

      case 'offer':
        // Viewer: received offer from broadcaster
        handleOffer(data.sdp);
        break;

      case 'answer':
        // Broadcaster: received answer from a viewer
        handleAnswer(data.from_id, data.sdp);
        break;

      case 'ice_candidate':
        handleIceCandidate(data.from_id, data.candidate, role);
        break;

      case 'chat':
        setChatMessages(prev => [...prev.slice(-199), data]);
        break;

      case 'tip_alert':
        setTipAlert({ name: data.tipper_name, amount: data.amount });
        setTimeout(() => setTipAlert(null), 4000);
        // Lovense: when running as broadcaster with SDK loaded, forward tip to toy
        if (role === 'broadcaster' && window.Lovense && typeof window.Lovense.receiveTip === 'function') {
          try {
            window.Lovense.receiveTip(data.tipper_name || 'Viewer', Number(data.amount) || 0);
          } catch (err) {
            console.warn('[Lovense] receiveTip failed:', err?.message);
          }
        }
        break;

      case 'stream_ended':
        toast.info('Stream has ended');
        cleanupAndReturnToLobby();
        break;

      case 'error':
        toast.error(data.message);
        break;

      default:
        break;
    }
  }, []);

  /* ───────── Broadcaster: Create offer for new viewer ───────── */
  const createOfferForViewer = useCallback(async (viewerId) => {
    const pc = new RTCPeerConnection({ iceServers });

    // Add local tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          target_id: viewerId,
          candidate: event.candidate.toJSON(),
        }));
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        cleanupViewerPC(viewerId);
      }
    };

    peerConnectionsRef.current[viewerId] = pc;

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'offer',
          target_id: viewerId,
          sdp: pc.localDescription.toJSON(),
        }));
      }
    } catch (err) {
      console.error('Failed to create offer:', err);
    }
  }, []);

  /* ───────── Broadcaster: Handle answer from viewer ───────── */
  const handleAnswer = useCallback(async (viewerId, sdp) => {
    const pc = peerConnectionsRef.current[viewerId];
    if (pc) {
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      } catch (err) {
        console.error('Failed to set remote description:', err);
      }
    }
  }, []);

  /* ───────── Viewer: Handle offer from broadcaster ───────── */
  const handleOffer = useCallback(async (sdp) => {
    const pc = new RTCPeerConnection({ iceServers });
    peerConnectionRef.current = pc;

    pc.ontrack = (event) => {
      if (remoteVideoRef.current && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          candidate: event.candidate.toJSON(),
        }));
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        setConnectionStatus('streaming');
      }
    };

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'answer',
          sdp: pc.localDescription.toJSON(),
        }));
      }
    } catch (err) {
      console.error('Failed to handle offer:', err);
    }
  }, []);

  /* ───────── ICE candidate handling ───────── */
  const handleIceCandidate = useCallback(async (fromId, candidate, role) => {
    try {
      const iceCandidate = new RTCIceCandidate(candidate);
      if (role === 'broadcaster') {
        const pc = peerConnectionsRef.current[fromId];
        if (pc) await pc.addIceCandidate(iceCandidate);
      } else {
        if (peerConnectionRef.current) await peerConnectionRef.current.addIceCandidate(iceCandidate);
      }
    } catch (err) {
      console.error('Failed to add ICE candidate:', err);
    }
  }, []);

  /* ───────── Cleanup helpers ───────── */
  const cleanupViewerPC = (viewerId) => {
    const pc = peerConnectionsRef.current[viewerId];
    if (pc) {
      pc.close();
      delete peerConnectionsRef.current[viewerId];
    }
  };

  const cleanupAndReturnToLobby = useCallback(() => {
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS; // Prevent reconnect
    // Stop any active recording
    if (mediaRecorderRef.current && typeof mediaRecorderRef.current.stop === 'function' && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch { /* already stopped */ }
    }
    recordingChunksRef.current = [];

    setMode('lobby');
    setMyStreamId(null);
    setActiveStream(null);
    setViewerCount(0);
    setChatMessages([]);
    setLeaderboard([]);
    setTokensReceived(0);
    setConnectionStatus('disconnected');
    setCameraOn(true);
    setMicOn(true);
    setShowSaveDialog(false);
    setUploading(false);
    setWatchingReplay(null);
  }, []);

  /* ───────── Go Live flow ───────── */
  const handleGoLive = async () => {
    if (!streamTitle.trim()) { toast.error('Enter a stream title'); return; }
    setStarting(true);

    let serverStreamId = null;   // track so we can roll back the DB record if init fails
    let mediaStream = null;

    try {
      // 1. Reuse preview stream or get new one
      let stream = previewStreamRef.current;
      if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
        if (sourceMode === 'screen') {
          stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        } else {
          const videoConstraint = selectedVideoId ? { deviceId: { exact: selectedVideoId } } : true;
          const audioConstraint = selectedAudioId ? { deviceId: { exact: selectedAudioId } } : true;
          stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: audioConstraint });
        }
      }
      mediaStream = stream;
      localStreamRef.current = stream;
      previewStreamRef.current = null; // Detach from preview
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      // 2. Create stream in DB
      const res = await axios.post(
        `${API}/api/streaming/start`,
        {
          title: streamTitle,
          description: streamDesc,
          token_goal: tokenGoal,
          guest_performer_ids: streamGuests.map(g => g.performer_id),
        },
        { withCredentials: true }
      );
      const streamId = res.data.stream_id;
      serverStreamId = streamId;
      setMyStreamId(streamId);

      // 3. Start recording (unless creator opted out)
      if (recordVod) {
        startRecording(stream);
      }

      // 4. Connect WebSocket as broadcaster
      connectWebSocket(streamId, 'broadcaster');

      setMode('broadcasting');
      setGoLiveOpen(false);
      toast.success(recordVod ? 'You are LIVE! Recording started.' : 'You are LIVE!');

      // Apply stream apps if any were set up before going live
      if (pendingApps.length > 0) {
        try {
          await axios.put(`${API}/api/stream-apps/stream/${streamId}`,
            { apps: pendingApps }, { withCredentials: true });
        } catch (e) { console.error('Failed to apply stream apps:', e); }
      }
    } catch (err) {
      // CRITICAL: if the server already created the stream record (step 2 succeeded
      // but a later step blew up), end it server-side so we don't orphan an open
      // "status:live" row that locks the user out of starting another stream.
      if (serverStreamId) {
        try {
          await axios.post(`${API}/api/streaming/end/${serverStreamId}`, {}, { withCredentials: true });
        } catch (cleanupErr) {
          console.error('[Go-Live] orphan cleanup failed (server may auto-cleanup in <4h):', cleanupErr);
        }
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach(t => t.stop());
      }
      localStreamRef.current = null;
      setMyStreamId(null);

      if (err.name === 'NotAllowedError' || err.name === 'NotFoundError') {
        toast.error('Camera/microphone access denied. Please allow access and try again.');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Failed to start stream');
      }
    } finally {
      setStarting(false);
    }
  };

  /* ───────── End stream ───────── */
  const handleEndStream = async () => {
    if (!myStreamId) {
      // Defensive: local state lost — try the bulk endpoint as a fallback
      if (window.confirm('Local stream state is missing. Force-end any live stream on your account?')) {
        try {
          const res = await axios.post(`${API}/api/streaming/end-all-mine`, {}, { withCredentials: true });
          toast.success(`Force-ended ${res.data.ended_count} stream(s)`);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Force-end failed');
        }
        cleanupAndReturnToLobby();
      }
      return;
    }
    if (!window.confirm('End your stream?')) return;

    // End stream on server — surface failures (no more silent catch)
    try {
      await axios.post(`${API}/api/streaming/end/${myStreamId}`, {}, { withCredentials: true });
    } catch (err) {
      console.error('[End Stream] server end failed:', err);
      toast.error(err.response?.data?.detail || 'End-stream API failed — cleaning up locally. Use Force-End from the lobby if you stay stuck.');
    }

    // Stop recording and show save dialog
    endedStreamIdRef.current = myStreamId;
    const blob = await stopRecording();

    // Close WebRTC / WS but keep mode for the save dialog
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }

    if (blob && blob.size > 0) {
      setShowSaveDialog(true);
      // Store blob temporarily on ref for the save handler
      mediaRecorderRef.current = blob;
    } else {
      toast.success('Stream ended');
      cleanupAndReturnToLobby();
    }
  };

  /* ───────── Join stream as viewer ───────── */
  const handleJoinStream = async (stream) => {
    try {
      // Check geo-blocking first
      const geoRes = await axios.get(`${API}/api/geo/check-stream/${stream.stream_id}`, { withCredentials: true });
      if (!geoRes.data.allowed) {
        const reason = geoRes.data.reason === 'country_blocked'
          ? `${t('live_blocked_region')} (${geoRes.data.country})`
          : `${t('live_blocked_region')} (${geoRes.data.region}, ${geoRes.data.country})`;
        toast.error(reason);
        return;
      }

      const res = await axios.post(`${API}/api/streaming/join/${stream.stream_id}`, {}, { withCredentials: true });
      setActiveStream(res.data);
      setTokensReceived(res.data.tokens_received || 0);
      setMode('viewing');
      connectWebSocket(stream.stream_id, 'viewer');
      fetchLeaderboard(stream.stream_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join stream');
    }
  };

  /* ───────── Leave stream (viewer) ───────── */
  const handleLeaveStream = () => {
    cleanupAndReturnToLobby();
  };

  /* ───────── Toggle camera/mic ───────── */
  const toggleCamera = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) { track.enabled = !track.enabled; setCameraOn(track.enabled); }
    }
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
    }
  };

  /* ───────── Chat send ───────── */
  const sendChatMessage = () => {
    if (!chatInput.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'chat', message: chatInput.trim() }));
    setChatInput('');
  };

  /* ───────── Tip ───────── */
  const handleTip = async () => {
    if (!activeStream) return;
    try {
      const res = await axios.post(
        `${API}/api/streaming/tip/${activeStream.stream_id}?amount=${tipAmount}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      fetchLeaderboard(activeStream.stream_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tip failed');
    }
  };

  const fetchLeaderboard = async (streamId) => {
    try {
      const res = await axios.get(`${API}/api/streaming/leaderboard/${streamId}`, { withCredentials: true });
      setLeaderboard(res.data);
    } catch { /* silent */ }
  };

  /* ───────── Fullscreen escape (viewer mode) ───────── */
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  /* ───────── Warn on page leave during broadcast ───────── */
  useEffect(() => {
    if (mode !== 'broadcasting') return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [mode]);

  /* ───────── Cleanup on unmount ───────── */
  useEffect(() => {
    return () => {
      if (localStreamRef.current) localStreamRef.current.getTracks().forEach(t => t.stop());
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
      if (peerConnectionRef.current) peerConnectionRef.current.close();
    };
  }, []);

  // ─────────────────────────────────────────
  // RENDER: Loading
  // ─────────────────────────────────────────
  if (loading && mode === 'lobby') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ─────────────────────────────────────────
  // RENDER: Broadcasting Mode
  // ─────────────────────────────────────────
  if (mode === 'broadcasting') {
    return (
      <div className="max-w-6xl mx-auto px-4 py-4" data-testid="broadcast-mode">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full flex items-center gap-1.5 animate-pulse">
              <span className="w-2 h-2 bg-white rounded-full" /> LIVE
            </span>
            <span className="text-slate-400 text-sm flex items-center gap-1">
              <Eye className="w-4 h-4" /> {viewerCount} viewer{viewerCount !== 1 ? 's' : ''}
            </span>
            <span className={`text-sm flex items-center gap-1 ${
              connectionStatus === 'connected' || connectionStatus === 'streaming' ? 'text-green-400' :
              connectionStatus === 'reconnecting' ? 'text-amber-400 animate-pulse' : 'text-amber-400'
            }`}>
              {connectionStatus === 'connected' || connectionStatus === 'streaming'
                ? <CellSignalHigh className="w-4 h-4" />
                : <CellSignalSlash className="w-4 h-4" />
              }
              {connectionStatus === 'reconnecting' ? 'reconnecting...' : connectionStatus}
            </span>
            <span className="px-2 py-1 bg-blue-900/40 text-blue-300 text-xs rounded-full flex items-center gap-1" data-testid="recording-indicator">
              <Record weight="fill" className="w-3 h-3 text-blue-400 animate-pulse" /> REC
            </span>
          </div>
          <button
            onClick={handleEndStream}
            className="px-5 py-2 bg-slate-800 text-red-400 rounded-xl font-semibold hover:bg-slate-700 transition-colors flex items-center gap-2"
            data-testid="end-stream-btn"
          >
            <Stop weight="fill" className="w-5 h-5" /> End Stream
          </button>
        </div>

        {/* Main grid: Video + Chat */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Video preview */}
          <div className="lg:col-span-2">
            <div ref={broadcastVideoContainerRef} className="relative rounded-2xl overflow-hidden bg-black aspect-video">
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
                data-testid="local-video"
              />
              {!cameraOn && (
                <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
                  <VideoCameraSlash className="w-16 h-16 text-slate-600" />
                </div>
              )}

              <AnimatePresence>
                {tipAlert && <TipAlert name={tipAlert.name} amount={tipAlert.amount} />}
              </AnimatePresence>

              {/* Compact edge overlays (viewers see these too — this is broadcaster's live preview) */}
              <EdgeStreamAppsOverlay
                streamId={myStreamId}
                onTip={undefined}
                leaderboard={leaderboard}
                tokensReceived={tokensReceived}
              />

              {/* Controls overlay */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
                <button
                  onClick={toggleCamera}
                  className={`p-3 rounded-full transition-colors ${cameraOn ? 'bg-slate-800/80 text-white hover:bg-slate-700' : 'bg-red-500 text-white'}`}
                  data-testid="toggle-camera-btn"
                  title={cameraOn ? 'Turn camera off' : 'Turn camera on'}
                  aria-label={cameraOn ? 'Turn camera off' : 'Turn camera on'}
                >
                  {cameraOn ? <VideoCamera weight="fill" className="w-5 h-5" /> : <VideoCameraSlash weight="fill" className="w-5 h-5" />}
                </button>
                <button
                  onClick={toggleMic}
                  className={`p-3 rounded-full transition-colors ${micOn ? 'bg-slate-800/80 text-white hover:bg-slate-700' : 'bg-red-500 text-white'}`}
                  data-testid="toggle-mic-btn"
                  title={micOn ? 'Mute microphone' : 'Unmute microphone'}
                  aria-label={micOn ? 'Mute microphone' : 'Unmute microphone'}
                >
                  {micOn ? <Microphone weight="fill" className="w-5 h-5" /> : <MicrophoneSlash weight="fill" className="w-5 h-5" />}
                </button>
                <button
                  onClick={async () => {
                    try {
                      const newStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
                      // Replace tracks
                      const oldStream = localStreamRef.current;
                      localStreamRef.current = newStream;
                      if (localVideoRef.current) localVideoRef.current.srcObject = newStream;
                      // Replace tracks on all peer connections
                      const videoTrack = newStream.getVideoTracks()[0];
                      Object.values(peerConnectionsRef.current).forEach(pc => {
                        const sender = pc.getSenders().find(s => s.track?.kind === 'video');
                        if (sender && videoTrack) sender.replaceTrack(videoTrack);
                      });
                      // Stop old tracks
                      if (oldStream) oldStream.getTracks().forEach(t => t.stop());
                      toast.success('Switched to screen share');
                    } catch { /* user cancelled */ }
                  }}
                  className="p-3 rounded-full bg-slate-800/80 text-white hover:bg-slate-700 transition-colors"
                  data-testid="share-screen-btn"
                  title="Share screen"
                >
                  <Broadcast weight="fill" className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowViewerPreview(v => !v)}
                  className={`p-3 rounded-full transition-colors ${showViewerPreview ? 'bg-blue-500 text-white' : 'bg-slate-800/80 text-white hover:bg-slate-700'}`}
                  data-testid="toggle-viewer-preview-btn"
                  title="Preview as viewer"
                  aria-label="Toggle preview as viewer"
                >
                  <Eye weight="fill" className="w-5 h-5" />
                </button>
              </div>

              {/* Preview-as-viewer PIP (broadcaster mirrors what viewers actually see) */}
              <ViewerPreviewPIP
                open={showViewerPreview}
                onClose={() => setShowViewerPreview(false)}
                localStream={localStreamRef.current}
                streamId={myStreamId}
                leaderboard={leaderboard}
                tokensReceived={tokensReceived}
                dragConstraintsRef={broadcastVideoContainerRef}
              />
            </div>

            {/* Token goal (if set) */}
            {tokenGoal > 0 && (
              <div className="mt-3 glass-light rounded-xl px-4 py-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400 flex items-center gap-1"><Target className="w-4 h-4" /> Token Goal</span>
                  <span className="text-amber-400 font-bold">{tokensReceived} / {tokenGoal}</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-amber-500 to-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (tokensReceived / tokenGoal) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Live Stream Apps editor (broadcaster) */}
            <details className="mt-3 glass-light rounded-xl group" data-testid="broadcaster-apps-panel">
              <summary className="cursor-pointer p-3 flex items-center justify-between text-sm font-medium text-slate-300 hover:text-white">
                <span className="flex items-center gap-2">
                  <Broadcast className="w-4 h-4 text-blue-400" />
                  Stream Apps & Overlays
                </span>
                <CaretDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="p-3 border-t border-white/5 max-h-[500px] overflow-y-auto">
                <StreamAppsConfigurator
                  streamId={myStreamId}
                  initialApps={pendingApps}
                  onSave={setPendingApps}
                />
              </div>
            </details>

            {/* Private Shows — broadcaster side */}
            <div className="mt-3">
              <PrivateShowCreatorWidget streamId={myStreamId} enabled={!!myStreamId} />
            </div>
          </div>

          {/* Chat */}
          <div className="glass-light rounded-2xl overflow-hidden flex flex-col h-[500px] lg:h-auto">
            <ChatPanel
              messages={chatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              onSend={sendChatMessage}
              isConnected={connectionStatus === 'connected' || connectionStatus === 'streaming'}
            />
          </div>
        </div>

        {/* Save Recording Dialog */}
        <SaveRecordingDialog
          open={showSaveDialog}
          uploading={uploading}
          uploadProgress={uploadProgress}
          recordingDuration={recordingDuration}
          vodAccess={vodAccess}
          setVodAccess={setVodAccess}
          vodPrice={vodPrice}
          setVodPrice={setVodPrice}
          onSave={() => uploadRecording(mediaRecorderRef.current)}
          onDiscard={() => { setShowSaveDialog(false); recordingChunksRef.current = []; endedStreamIdRef.current = null; cleanupAndReturnToLobby(); }}
        />
      </div>
    );
  }

  // ─────────────────────────────────────────
  // RENDER: OBS Monitoring Mode (creator is broadcasting via OBS ingest,
  // but uses this page to see chat / tips / viewer count / stream status)
  // ─────────────────────────────────────────
  if (mode === 'obs_monitoring' && activeStream) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-4" data-testid="obs-monitor">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full flex items-center gap-1.5 animate-pulse">
              <span className="w-2 h-2 bg-white rounded-full" /> OBS LIVE
            </span>
            <span className="px-2 py-1 bg-slate-800 text-slate-300 text-sm rounded-full flex items-center gap-1" data-testid="viewer-count">
              <Eye className="w-4 h-4" /> {viewerCount}
            </span>
            <span className="px-2 py-1 bg-amber-900/40 text-amber-300 text-xs rounded-full flex items-center gap-1">
              <Coins weight="fill" className="w-3 h-3" /> {tokensReceived} earned
            </span>
          </div>
          <button
            onClick={async () => {
              try {
                await axios.post(`${API}/api/streaming/end/${myStreamId}`, {}, { withCredentials: true });
              } catch { /* server may already have ended */ }
              cleanupAndReturnToLobby();
              toast.success('Stream ended on LustLink. Stop OBS separately to release the encoder.');
            }}
            className="px-5 py-2 bg-slate-800 text-red-400 rounded-xl font-semibold hover:bg-slate-700 transition-colors flex items-center gap-2"
            data-testid="end-obs-monitor-btn"
          >
            <Stop weight="fill" className="w-5 h-5" /> End Stream
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: OBS ingest indicator + edge overlays preview */}
          <div className="lg:col-span-2 space-y-3">
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center">
              <div className="text-center px-6" data-testid="obs-ingest-status">
                <Broadcast weight="fill" className="w-16 h-16 text-blue-500 mx-auto mb-3 animate-pulse" />
                <p className="font-heading text-xl text-white">OBS Ingesting</p>
                <p className="text-slate-400 mt-1 text-sm">Your OBS feed is being pushed to Cloudflare and broadcast to viewers.</p>
                <p className="text-slate-600 mt-2 text-xs">To preview what viewers see, join your own stream in another tab.</p>
              </div>

              <AnimatePresence>
                {tipAlert && <TipAlert name={tipAlert.name} amount={tipAlert.amount} />}
              </AnimatePresence>

              {/* Edge overlays render here to confirm to broadcaster that they're active */}
              <EdgeStreamAppsOverlay
                streamId={activeStream.stream_id}
                onTip={undefined}
                leaderboard={leaderboard}
                tokensReceived={tokensReceived}
              />
            </div>

            <div className="glass-light rounded-xl p-4">
              <h2 className="font-heading text-lg font-bold text-white">{activeStream.title}</h2>
              {activeStream.description && <p className="text-sm text-slate-500 mt-2">{activeStream.description}</p>}
              {activeStream.token_goal > 0 && (
                <div className="mt-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-400 flex items-center gap-1"><Target className="w-4 h-4" /> Token Goal</span>
                    <span className="text-amber-400 font-bold">{tokensReceived} / {activeStream.token_goal}</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-amber-500 to-blue-500 h-2 rounded-full transition-all"
                      style={{ width: `${Math.min(100, (tokensReceived / activeStream.token_goal) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Chat sidebar */}
          <div className="glass-light rounded-2xl overflow-hidden flex flex-col h-[500px] lg:h-auto">
            <ChatPanel
              messages={chatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              onSend={sendChatMessage}
              isConnected={connectionStatus === 'connected' || connectionStatus === 'streaming'}
            />
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────
  // RENDER: Viewing Mode
  // ─────────────────────────────────────────
  if (mode === 'viewing' && activeStream) {
    const goalPercent = activeStream.token_goal > 0
      ? Math.min(100, (tokensReceived / activeStream.token_goal) * 100)
      : 0;

    // ─── Fullscreen viewer (video + right chat sidebar) ───
    if (isFullscreen) {
      return (
        <div className="fixed inset-0 z-[100] bg-black flex" data-testid="fullscreen-viewer">
          {/* Video takes remaining space; chat panel sits on the right */}
          <div className="flex-1 relative bg-black">
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-contain"
              data-testid="fullscreen-remote-video"
            />

            {/* Top-left LIVE + viewer count */}
            <div className="absolute top-4 left-4 flex items-center gap-2 z-30">
              <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full flex items-center gap-1.5 animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full" /> LIVE
              </span>
              <span className="px-2 py-1 bg-black/60 text-white text-sm rounded-full flex items-center gap-1">
                <Eye className="w-4 h-4" /> {viewerCount}
              </span>
            </div>

            {/* Top-right controls: chat toggle + exit fullscreen */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-30">
              <button
                onClick={() => setShowFullscreenChat(v => !v)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${showFullscreenChat ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-black/60 text-white hover:bg-black/80'}`}
                data-testid="toggle-fullscreen-chat-btn"
                title={showFullscreenChat ? 'Hide chat' : 'Show chat'}
                aria-label={showFullscreenChat ? 'Hide chat' : 'Show chat'}
              >
                <ChatCircleText weight="fill" className="w-4 h-4" />
                {showFullscreenChat ? 'Hide chat' : 'Show chat'}
              </button>
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                data-testid="exit-fullscreen-btn"
                title="Exit fullscreen (Esc)"
                aria-label="Exit fullscreen"
              >
                <CornersIn weight="fill" className="w-5 h-5" />
              </button>
            </div>

            <AnimatePresence>
              {tipAlert && <TipAlert name={tipAlert.name} amount={tipAlert.amount} />}
            </AnimatePresence>

            {/* Edge overlays (viewers see these) */}
            <EdgeStreamAppsOverlay
              streamId={activeStream.stream_id}
              onTip={async (amount, label) => {
                await axios.post(
                  `${API}/api/streaming/tip/${activeStream.stream_id}?amount=${amount}`,
                  {},
                  { withCredentials: true }
                );
                fetchLeaderboard(activeStream.stream_id);
              }}
              leaderboard={leaderboard}
              tokensReceived={tokensReceived}
            />
          </div>

          {/* Chat sidebar */}
          {showFullscreenChat && (
            <div className="w-[320px] max-w-[35vw] h-full flex flex-col bg-[#0b1740]/95 backdrop-blur-md border-l border-white/10" data-testid="fullscreen-chat-sidebar">
              <ChatPanel
                messages={chatMessages}
                chatInput={chatInput}
                setChatInput={setChatInput}
                onSend={sendChatMessage}
                isConnected={connectionStatus === 'connected' || connectionStatus === 'streaming'}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="max-w-6xl mx-auto px-4 py-4" data-testid="stream-viewer">
        <button
          onClick={handleLeaveStream}
          className="text-slate-400 hover:text-white mb-4 text-sm flex items-center gap-1"
          data-testid="back-to-streams-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to streams
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Video + controls */}
          <div className="lg:col-span-2 space-y-3">
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
                data-testid="remote-video"
              />

              {connectionStatus !== 'streaming' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                  <div className="text-center">
                    <Broadcast weight="fill" className="w-16 h-16 text-red-500 mx-auto mb-2 animate-pulse" />
                    <p className="text-white font-heading text-xl">{activeStream.title}</p>
                    <p className="text-slate-400 mt-1">{activeStream.creator_name}</p>
                    <p className="text-slate-500 text-sm mt-2">
                      {connectionStatus === 'connected' ? 'Waiting for video...' :
                       connectionStatus === 'reconnecting' ? 'Reconnecting...' : 'Connecting...'}
                    </p>
                  </div>
                </div>
              )}

              {/* Live badge overlay */}
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="px-3 py-1 bg-red-500 text-white text-sm font-bold rounded-full flex items-center gap-1.5 animate-pulse">
                  <span className="w-2 h-2 bg-white rounded-full" /> LIVE
                </span>
                <span className="px-2 py-1 bg-black/60 text-white text-sm rounded-full flex items-center gap-1">
                  <Eye className="w-4 h-4" /> {viewerCount}
                </span>
              </div>

              {/* Fullscreen toggle (viewer) */}
              <button
                onClick={() => setIsFullscreen(true)}
                className="absolute top-4 right-4 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors z-20"
                data-testid="enter-fullscreen-btn"
                title="Fullscreen (chat stays visible)"
                aria-label="Enter fullscreen"
              >
                <CornersOut weight="fill" className="w-5 h-5" />
              </button>

              <AnimatePresence>
                {tipAlert && <TipAlert name={tipAlert.name} amount={tipAlert.amount} />}
              </AnimatePresence>

              {/* Compact edge overlays — the "on-video" stream apps viewers see */}
              <EdgeStreamAppsOverlay
                streamId={activeStream.stream_id}
                onTip={async (amount, label) => {
                  await axios.post(
                    `${API}/api/streaming/tip/${activeStream.stream_id}?amount=${amount}`,
                    {},
                    { withCredentials: true }
                  );
                  fetchLeaderboard(activeStream.stream_id);
                }}
                leaderboard={leaderboard}
                tokensReceived={tokensReceived}
              />
            </div>

            {/* Stream info */}
            <div className="glass-light rounded-xl p-4">
              <h2 className="font-heading text-lg font-bold text-white">{activeStream.title}</h2>
              <p className="text-sm text-slate-400 flex items-center gap-1 mt-1">
                <User className="w-4 h-4" /> {activeStream.creator_name}
              </p>
              {activeStream.description && <p className="text-sm text-slate-500 mt-2">{activeStream.description}</p>}
            </div>

            {/* Token Goal */}
            {activeStream.token_goal > 0 && (
              <div className="glass-light rounded-xl px-4 py-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400 flex items-center gap-1"><Target className="w-4 h-4" /> Token Goal</span>
                  <span className="text-amber-400 font-bold">{tokensReceived} / {activeStream.token_goal}</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-amber-500 to-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${goalPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Tip controls (single manual entry, no presets) */}
            <div className="glass-light rounded-xl p-4 flex items-center gap-3" data-testid="tip-controls">
              <label htmlFor="tip-amount-input" className="text-sm text-slate-400 whitespace-nowrap">Tip Amount</label>
              <input
                id="tip-amount-input"
                type="number"
                min="1"
                max="10000"
                value={tipAmount}
                onChange={(e) => setTipAmount(Math.max(1, parseInt(e.target.value) || 1))}
                className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                data-testid="tip-amount-input"
                placeholder="Enter tokens"
              />
              <button
                onClick={handleTip}
                className="px-5 py-2.5 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-colors flex items-center gap-2 whitespace-nowrap"
                data-testid="send-tip-btn"
              >
                <Coins weight="fill" className="w-5 h-5" /> Tip {tipAmount}
              </button>
            </div>

            {/* Stream Apps (removed from sidebar — now rendered as edge overlays on the video itself) */}

            {/* Leaderboard */}
            <TipLeaderboard leaderboard={leaderboard} />

            {/* Private Shows — viewer side */}
            <PrivateShowViewerWidget streamId={activeStream.stream_id} isSelf={false} />
          </div>

          {/* Chat */}
          <div className="glass-light rounded-2xl overflow-hidden flex flex-col h-[500px] lg:h-auto">
            <ChatPanel
              messages={chatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              onSend={sendChatMessage}
              isConnected={connectionStatus === 'connected' || connectionStatus === 'streaming'}
            />
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────
  // RENDER: Watching Replay (VOD)
  // ─────────────────────────────────────────
  if (watchingReplay) {
    return (
      <ReplayViewer
        replay={watchingReplay}
        onBack={() => setWatchingReplay(null)}
        replayVideoRef={replayVideoRef}
      />
    );
  }

  // ─────────────────────────────────────────
  // RENDER: Lobby Mode
  // ─────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="live-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
            <Broadcast weight="fill" className="w-8 h-8 text-red-500" />
            {t('live_title')}
          </h1>
        </div>

        {(user?.role === 'creator' || user?.role === 'admin') && (
          <GoLiveDialog
            open={goLiveOpen}
            onOpenChange={setGoLiveOpen}
            t={t}
            streamMode={streamMode}
            setStreamMode={setStreamMode}
            sourceMode={sourceMode}
            setSourceMode={setSourceMode}
            streamTitle={streamTitle}
            setStreamTitle={setStreamTitle}
            streamDesc={streamDesc}
            setStreamDesc={setStreamDesc}
            tokenGoal={tokenGoal}
            setTokenGoal={setTokenGoal}
            recordVod={recordVod}
            setRecordVod={setRecordVod}
            obsKeyData={obsKeyData}
            showObsKey={showObsKey}
            setShowObsKey={setShowObsKey}
            obsGoingLive={obsGoingLive}
            onStartObs={handleStartObs}
            previewVideoRef={previewVideoRef}
            videoDevices={videoDevices}
            audioDevices={audioDevices}
            selectedVideoId={selectedVideoId}
            setSelectedVideoId={setSelectedVideoId}
            selectedAudioId={selectedAudioId}
            setSelectedAudioId={setSelectedAudioId}
            myPerformerId={myPerformerId}
            streamGuests={streamGuests}
            setStreamGuests={setStreamGuests}
            showGuestsPanel={showGuestsPanel}
            setShowGuestsPanel={setShowGuestsPanel}
            pendingApps={pendingApps}
            setPendingApps={setPendingApps}
            showAppsPanel={showAppsPanel}
            setShowAppsPanel={setShowAppsPanel}
            blockedCountries={blockedCountries}
            blockedStates={blockedStates}
            starting={starting}
            onStartLive={handleGoLive}
          />
        )}
      </div>

      {/* Orphan stream recovery banner */}
      {orphanStream && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex items-center gap-3" data-testid="orphan-stream-banner">
          <ShieldWarning weight="fill" className="w-7 h-7 text-amber-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-amber-100 font-semibold">You have a live stream from earlier</p>
            <p className="text-xs text-amber-200/70 truncate">
              &quot;{orphanStream.title}&quot; · {orphanStream.mode === 'obs' ? 'OBS' : 'Browser'} · started {new Date(orphanStream.started_at).toLocaleTimeString()}
            </p>
          </div>
          <button
            onClick={resumeOrphanStream}
            disabled={orphanBusy}
            className="px-4 py-2 bg-amber-500 text-amber-950 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50"
            data-testid="orphan-resume-btn"
          >Resume</button>
          <button
            onClick={forceEndOrphanStream}
            disabled={orphanBusy}
            className="px-4 py-2 bg-red-500/20 text-red-300 rounded-lg text-sm font-semibold hover:bg-red-500/30 transition-colors disabled:opacity-50"
            data-testid="orphan-force-end-btn"
          >{orphanBusy ? 'Ending…' : 'Force-End'}</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-900/50 rounded-xl p-1 w-fit" data-testid="lobby-tabs">
        <button
          onClick={() => setLobbyTab('live')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
            lobbyTab === 'live' ? 'bg-red-500/20 text-red-400' : 'text-slate-500 hover:text-slate-300'
          }`}
          data-testid="tab-live"
        >
          <Broadcast weight={lobbyTab === 'live' ? 'fill' : 'regular'} className="w-4 h-4" />
          Live Now {streams.length > 0 && <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">{streams.length}</span>}
        </button>
        <button
          onClick={() => setLobbyTab('replays')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
            lobbyTab === 'replays' ? 'bg-indigo-500/20 text-indigo-400' : 'text-slate-500 hover:text-slate-300'
          }`}
          data-testid="tab-replays"
        >
          <Rewind weight={lobbyTab === 'replays' ? 'fill' : 'regular'} className="w-4 h-4" />
          Replays
        </button>
      </div>

      {/* Live Tab */}
      {lobbyTab === 'live' && (
        <>
          {streams.length === 0 ? (
            <div className="text-center py-20">
              <Broadcast className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-white mb-2">No live streams</h2>
              <p className="text-slate-500 max-w-sm mx-auto">
                {(user?.role === 'creator' || user?.role === 'admin')
                  ? "Be the first to go live! Click the button above to start streaming."
                  : "Check back later — creators will be streaming soon!"}
              </p>
              <div className="mt-6 max-w-md mx-auto p-4 bg-slate-800/50 rounded-xl text-left">
                <p className="text-slate-400 text-sm"><strong className="text-slate-300">About Live Streaming:</strong> Creators broadcast live to their audience using real-time video (WebRTC). Viewers can watch, chat, and tip using tokens. Broadcasters can set token goals and see a live tip leaderboard. <strong className="text-slate-300">Only the broadcasting creator</strong> can save their stream as a VOD replay or list it for sale — viewers cannot record other people's streams on the platform.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {streams.map((stream, idx) => (
                <motion.div
                  key={stream.stream_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="glass-light rounded-2xl overflow-hidden cursor-pointer hover:ring-1 hover:ring-red-500/30 transition-all group"
                  onClick={() => handleJoinStream(stream)}
                  data-testid={`stream-card-${stream.stream_id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-red-900/30 to-indigo-900/30 relative flex items-center justify-center">
                    {stream.creator_avatar ? (
                      <img src={stream.creator_avatar} alt="" className="w-full h-full object-cover opacity-40" />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center group-hover:bg-red-500/40 transition-colors">
                        <Play weight="fill" className="w-8 h-8 text-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center gap-1 animate-pulse">
                        <span className="w-1.5 h-1.5 bg-white rounded-full" /> LIVE
                      </span>
                    </div>
                    <div className="absolute top-3 right-3 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {stream.viewer_count}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-white truncate">{stream.title}</h3>
                    <p className="text-sm text-slate-400 flex items-center gap-1 mt-1">
                      <User className="w-4 h-4" /> {stream.creator_name}
                    </p>
                    {stream.token_goal > 0 && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-500">Goal</span>
                          <span className="text-amber-400">{stream.tokens_received}/{stream.token_goal}</span>
                        </div>
                        <div className="w-full bg-slate-800 rounded-full h-1.5">
                          <div
                            className="bg-amber-500 h-1.5 rounded-full"
                            style={{ width: `${Math.min(100, (stream.tokens_received / stream.token_goal) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Replays Tab */}
      {lobbyTab === 'replays' && (
        <>
          {loadingReplays ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : replays.length === 0 ? (
            <div className="text-center py-20">
              <Rewind className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-white mb-2">No replays yet</h2>
              <p className="text-slate-500 max-w-sm mx-auto">
                Stream replays will appear here after creators save their live sessions.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {replays.map((replay, idx) => (
                <motion.div
                  key={replay.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="glass-light rounded-2xl overflow-hidden cursor-pointer hover:ring-1 hover:ring-indigo-500/30 transition-all group"
                  onClick={() => setWatchingReplay(replay)}
                  data-testid={`replay-card-${replay.id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-indigo-900/30 to-blue-900/30 relative flex items-center justify-center">
                    {replay.creator_avatar ? (
                      <img src={replay.creator_avatar} alt="" className="w-full h-full object-cover opacity-30" />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-indigo-500/20 flex items-center justify-center group-hover:bg-indigo-500/40 transition-colors">
                        <Play weight="fill" className="w-8 h-8 text-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        <Rewind className="w-3 h-3" /> REPLAY
                      </span>
                    </div>
                    {replay.duration_seconds > 0 && (
                      <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/70 text-white text-xs rounded-full">
                        {Math.floor(replay.duration_seconds / 60)}:{String(Math.round(replay.duration_seconds % 60)).padStart(2, '0')}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-white truncate">{replay.title}</h3>
                    <div className="flex items-center gap-3 mt-1.5 text-sm text-slate-400">
                      <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {replay.creator_name}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {replay.views_count}</span>
                      {replay.tokens_received > 0 && (
                        <span className="flex items-center gap-1"><Coins weight="fill" className="w-3.5 h-3.5 text-amber-400" /> {replay.tokens_received}</span>
                      )}
                    </div>
                    {/* Access tier + price badge */}
                    <div className="mt-2 flex items-center gap-2">
                      {replay.access === 'paid' && replay.price_tokens > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs font-bold rounded-full" data-testid={`replay-price-${replay.id}`}>
                          <Coins weight="fill" className="w-3 h-3" /> {replay.price_tokens} tokens
                        </span>
                      ) : replay.access === 'subscribers_only' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-xs font-bold rounded-full" data-testid={`replay-subs-only-${replay.id}`}>
                          Subscribers only
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full" data-testid={`replay-free-${replay.id}`}>
                          Free
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LivePage;
